import { cx } from "../../lib/cx.js";
import { IconProvider } from "../Icon/IconProvider.js";
import { PortalHostContext, usePortalHost } from "../_overlay/PortalHost.js";
import { useMediaQuery } from "../../lib/useMediaQuery.js";
import { BrandContext } from "./BrandContext.js";
import { applyBrandAccent, brandAccentStyle } from "../../lib/accent.js";
import { brandAttributes } from "../../themes/types.js";
/* empty css              */
import { useLayoutEffect, useMemo } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/ThemeRoot/ThemeRoot.tsx
var DARK_QUERY = "(prefers-color-scheme: dark)";
/** The accent's inline properties, or none (with a warning) for an invalid accent. */
function accentStyle(accent, surfaces) {
	if (!accent) return void 0;
	try {
		return brandAccentStyle(accent, { surfaces }).style;
	} catch (error) {
		console.warn(`ThemeRoot: ignoring the accent, using the pack's primary. ${String(error)}`);
		return;
	}
}
/**
* Applies a brand pack, mode, density and tenant accent, and provides the brand to
* everything inside it: `useBrand()`, BrandMark and AuthLayout defaults, and the pack's
* icons (IconProvider).
*/
function ThemeRoot({ brand, mode = "light", accent = brand.accent, density = brand.density, target = "element", className, style, children, ...rest }) {
	const prefersDark = useMediaQuery(DARK_QUERY);
	const resolvedMode = mode === "system" ? prefersDark ? "dark" : "light" : mode;
	const attributes = brandAttributes(brand.pack, {
		mode: resolvedMode,
		density
	});
	const surfaces = brand.pack.surfaces;
	const accentProperties = useMemo(() => accentStyle(accent, surfaces), [accent, surfaces]);
	const theDocument = target === "document";
	const { host, hostElement } = usePortalHost();
	const [brandId, theme, densityValue] = [
		attributes["data-brand"],
		attributes["data-theme"],
		attributes["data-density"]
	];
	useLayoutEffect(() => {
		if (!theDocument) return;
		const html = document.documentElement;
		const next = {
			"data-brand": brandId,
			"data-theme": theme,
			"data-density": densityValue
		};
		const previous = Object.fromEntries(Object.keys(next).map((name) => [name, html.getAttribute(name)]));
		for (const [name, value] of Object.entries(next)) html.setAttribute(name, value);
		return () => {
			for (const [name, value] of Object.entries(previous)) if (value === null) html.removeAttribute(name);
			else html.setAttribute(name, value);
		};
	}, [
		theDocument,
		brandId,
		theme,
		densityValue
	]);
	useLayoutEffect(() => {
		if (!theDocument || !accentProperties) return;
		const html = document.documentElement;
		for (const [name, value] of Object.entries(accentProperties)) html.style.setProperty(name, value);
		return () => {
			applyBrandAccent(html, null);
		};
	}, [theDocument, accentProperties]);
	const content = /* @__PURE__ */ jsx(BrandContext.Provider, {
		value: brand,
		children: /* @__PURE__ */ jsx(PortalHostContext.Provider, {
			value: theDocument ? void 0 : host,
			children: /* @__PURE__ */ jsx(IconProvider, {
				icons: brand.pack.icons,
				children
			})
		})
	});
	if (theDocument) return content;
	return /* @__PURE__ */ jsxs("div", {
		...rest,
		...attributes,
		className: cx("ds-theme-root", className),
		style: {
			...accentProperties,
			...style
		},
		children: [content, hostElement]
	});
}
//#endregion
export { ThemeRoot };

//# sourceMappingURL=ThemeRoot.js.map